import { useState, useEffect } from "react";
import { usePageContent } from "@/hooks/usePageContent";
import { settingsApi } from "@/integrations/mysql/client";
import { useToast } from "@/hooks/use-toast";
import {
  Save, Loader2, MessageCircle, Phone, Mail, Globe, MapPin,
  Plus, Trash2, ChevronUp, ChevronDown, Eye, EyeOff, Pencil, X,
  Users, Star, Sparkles, Heart, Award, Shield, Clock, CheckCircle, Zap, Target, type LucideIcon,
} from "lucide-react";

// ── Badge icon helpers ─────────────────────────────────────────────────────────
interface BadgeItem { label: string; icon: string; }

const BADGE_ICON_MAP: Record<string, LucideIcon> = {
  MessageCircle, Mail, MapPin, Phone, Globe,
  Users, Star, Sparkles, Heart, Award, Shield, Clock, CheckCircle, Zap, Target,
};

function parseBadges(raw: any[]): BadgeItem[] {
  return raw.map(b => typeof b === 'string' ? { label: b, icon: 'Star' } : b);
}

function BadgeIconPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const Icon = BADGE_ICON_MAP[value] || Star;
  return (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`w-8 h-8 flex items-center justify-center rounded-lg border transition-colors ${
          open ? "border-brand-primary bg-brand-primary/8" : "border-gray-200 bg-white hover:border-brand-primary/50"
        }`}
        title={`Ícone: ${value}`}
      >
        <Icon className="w-4 h-4 text-brand-primary" />
      </button>
      {open && (
        <div className="absolute left-0 top-10 z-30 bg-white border border-gray-200 rounded-xl shadow-xl p-2 w-48">
          <div className="grid grid-cols-5 gap-1">
            {Object.entries(BADGE_ICON_MAP).map(([name, Ic]) => (
              <button
                key={name}
                type="button"
                title={name}
                onClick={() => { onChange(name); setOpen(false); }}
                className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all ${
                  value === name
                    ? "bg-brand-primary text-white"
                    : "text-gray-500 hover:bg-brand-primary/10 hover:text-brand-primary"
                }`}
              >
                <Ic className="w-4 h-4" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
import { FaWhatsapp, FaInstagram, FaFacebook, FaYoutube, FaTiktok } from "react-icons/fa";

// ── Shared UI ──────────────────────────────────────────────────────────────────
function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{label}</label>
      {hint && <p className="text-xs text-gray-400">{hint}</p>}
      {children}
    </div>
  );
}
function TextInput({ value, onChange, placeholder, maxLength, type = "text" }: {
  value: string; onChange: (v: string) => void; placeholder?: string; maxLength?: number; type?: string;
}) {
  return (
    <input type={type} value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} maxLength={maxLength}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all" />
  );
}
function TextArea({ value, onChange, placeholder, rows = 3, maxLength }: {
  value: string; onChange: (v: string) => void; placeholder?: string; rows?: number; maxLength?: number;
}) {
  return (
    <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      rows={rows} maxLength={maxLength}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all resize-none" />
  );
}
function SectionHeader({ icon: Icon, title, description }: { icon: React.ElementType; title: string; description: string }) {
  return (
    <div className="flex items-center gap-3 pb-3 border-b border-gray-100 mb-4">
      <div className="w-8 h-8 rounded-lg bg-brand-primary/8 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-brand-primary" />
      </div>
      <div>
        <h3 className="text-sm font-bold text-gray-800">{title}</h3>
        <p className="text-xs text-gray-400">{description}</p>
      </div>
    </div>
  );
}
function SaveBar({ dirty, saving, onSave }: { dirty: boolean; saving: boolean; onSave: () => void }) {
  if (!dirty) return null;
  return (
    <div className="flex items-center justify-between bg-brand-primary/5 border border-brand-primary/20 rounded-xl px-4 py-3 mt-2">
      <p className="text-xs text-brand-primary font-medium">Alterações não salvas</p>
      <button onClick={onSave} disabled={saving}
        className="flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary/90 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60">
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
        Salvar
      </button>
    </div>
  );
}

// ── Channel types ──────────────────────────────────────────────────────────────
export type ChannelType = 'whatsapp' | 'email' | 'instagram' | 'phone' | 'facebook' | 'youtube' | 'tiktok' | 'link';

export interface ContactChannel {
  id: string;
  type: ChannelType;
  label: string;
  value: string;
  description: string;
  cta: string;
  enabled: boolean;
}

export const CHANNEL_META: Record<ChannelType, {
  label: string;
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  placeholder: string;
  hint: string;
  buildHref: (v: string) => string;
}> = {
  whatsapp:  { label: 'WhatsApp',  icon: FaWhatsapp,  iconBg: 'bg-green-100', iconColor: 'text-green-600', placeholder: '5511998874081', hint: 'Só números com DDI e DDD. Ex: 5511998874081', buildHref: v => `https://wa.me/${v.replace(/\D/g,'')}` },
  email:     { label: 'E-mail',    icon: Mail,        iconBg: 'bg-brand-primary/10', iconColor: 'text-brand-primary', placeholder: 'contato@clinica.com.br', hint: 'Endereço de e-mail de contato', buildHref: v => `mailto:${v}` },
  instagram: { label: 'Instagram', icon: FaInstagram, iconBg: 'bg-pink-50',    iconColor: 'text-pink-500', placeholder: 'https://www.instagram.com/...', hint: 'URL completa do perfil', buildHref: v => v },
  phone:     { label: 'Telefone',  icon: Phone,       iconBg: 'bg-blue-50',   iconColor: 'text-blue-500', placeholder: '+55 11 99887-4081', hint: 'Número de telefone exibido', buildHref: v => `tel:${v.replace(/\D/g,'')}` },
  facebook:  { label: 'Facebook',  icon: FaFacebook,  iconBg: 'bg-blue-50',   iconColor: 'text-blue-600', placeholder: 'https://www.facebook.com/...', hint: 'URL completa da página', buildHref: v => v },
  youtube:   { label: 'YouTube',   icon: FaYoutube,   iconBg: 'bg-red-50',    iconColor: 'text-red-500',  placeholder: 'https://www.youtube.com/...', hint: 'URL do canal no YouTube', buildHref: v => v },
  tiktok:    { label: 'TikTok',    icon: FaTiktok,    iconBg: 'bg-gray-100',  iconColor: 'text-gray-800', placeholder: 'https://www.tiktok.com/@...', hint: 'URL do perfil no TikTok', buildHref: v => v },
  link:      { label: 'Link',      icon: Globe,       iconBg: 'bg-gray-100',  iconColor: 'text-gray-600', placeholder: 'https://...', hint: 'URL de destino do link', buildHref: v => v },
};

const CHANNEL_TYPES: ChannelType[] = ['whatsapp','email','instagram','phone','facebook','youtube','tiktok','link'];

export const DEFAULT_CHANNELS: ContactChannel[] = [
  { id: 'whatsapp', type: 'whatsapp', label: 'WhatsApp', value: '5511998874081', description: 'Agendamentos, dúvidas e retornos de forma rápida.', cta: 'Enviar mensagem', enabled: true },
  { id: 'email',    type: 'email',    label: 'E-mail',   value: 'brenda.ramos.nutri@gmail.com', description: 'Para solicitações formais ou propostas de parceria.', cta: 'Enviar e-mail', enabled: true },
  { id: 'instagram',type: 'instagram',label: 'Instagram',value: 'https://www.instagram.com/brenda.nutricionista/', description: 'Dicas diárias, receitas e conteúdo exclusivo de nutrição.', cta: 'Seguir no Instagram', enabled: true },
];

function parseChannels(raw: string | undefined): ContactChannel[] {
  if (!raw) return DEFAULT_CHANNELS;
  try {
    const p = JSON.parse(raw);
    return Array.isArray(p) && p.length > 0 ? p : DEFAULT_CHANNELS;
  } catch { return DEFAULT_CHANNELS; }
}

// ── Cabeçalho tab ──────────────────────────────────────────────────────────────
function CabecalhoTab() {
  const { getText, get, upsert, loading } = usePageContent('contact');
  const { toast } = useToast();
  const DEFAULT_BADGES: BadgeItem[] = [
    { label: 'WhatsApp Disponível', icon: 'MessageCircle' },
    { label: 'Resposta em 24h Úteis', icon: 'Mail' },
    { label: '3 Consultórios em SP', icon: 'MapPin' },
  ];

  const [title, setTitle]      = useState('');
  const [desc, setDesc]        = useState('');
  const [badges, setBadges]    = useState<BadgeItem[]>(DEFAULT_BADGES);
  const [badgeDraft, setBadgeDraft] = useState('');
  const [saving, setSaving]    = useState(false);
  const [dirty, setDirty]      = useState(false);

  const origTitle = getText('hero', 'title', 'Entre em Contato');
  const origDesc  = getText('hero', 'description', 'Estou aqui para ajudar você em sua jornada de transformação. Escolha o canal mais conveniente e vamos conversar!');
  const rawBadges = get('hero', 'badges')?.content_value?.items as any[] | undefined;
  const origBadges: BadgeItem[] = rawBadges ? parseBadges(rawBadges) : DEFAULT_BADGES;

  useEffect(() => {
    if (loading) return;
    setTitle(origTitle);
    setDesc(origDesc);
    setBadges(origBadges);
    setDirty(false);
  }, [loading]);

  const set = <T,>(fn: (v: T) => void) => (v: T) => { fn(v); setDirty(true); };

  const addBadge = () => {
    const t = badgeDraft.trim();
    if (!t) return;
    setBadges(p => [...p, { label: t, icon: 'Star' }]);
    setBadgeDraft('');
    setDirty(true);
  };
  const editBadgeLabel = (i: number, v: string) => { setBadges(p => p.map((b, idx) => idx === i ? { ...b, label: v } : b)); setDirty(true); };
  const editBadgeIcon  = (i: number, v: string) => { setBadges(p => p.map((b, idx) => idx === i ? { ...b, icon: v }  : b)); setDirty(true); };
  const removeBadge = (i: number) => { setBadges(p => p.filter((_, idx) => idx !== i)); setDirty(true); };
  const moveBadge = (i: number, dir: -1 | 1) => {
    setBadges(prev => {
      const arr = [...prev];
      const ni = i + dir;
      if (ni < 0 || ni >= arr.length) return arr;
      [arr[i], arr[ni]] = [arr[ni], arr[i]];
      return arr;
    });
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        upsert('hero', 'title',       { text: title }),
        upsert('hero', 'description', { text: desc }),
        upsert('hero', 'badges',      { items: badges }),
      ]);
      toast({ title: 'Cabeçalho salvo!' });
      setDirty(false);
    } catch {
      toast({ title: 'Erro ao salvar', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 bg-white border border-gray-100 rounded-xl p-4">
      <SectionHeader icon={MessageCircle} title="Cabeçalho da Página de Contato" description="Seção verde no topo da página /contato" />

      <Field label="Título">
        <TextInput value={title} onChange={set(setTitle)} placeholder="Entre em Contato" maxLength={100} />
      </Field>
      <Field label={`Descrição (${desc.length}/500)`}>
        <TextArea value={desc} onChange={set(setDesc)} rows={3} maxLength={500} placeholder="Estou aqui para ajudar..." />
      </Field>

      {/* Badges */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Badges ({badges.length})</label>
          <span className="text-xs text-gray-400">Chips exibidos abaixo da descrição</span>
        </div>

        {badges.map((b, i) => (
          <div key={i} className="flex items-center gap-2">
            <BadgeIconPicker value={b.icon} onChange={v => editBadgeIcon(i, v)} />
            <input
              type="text" value={b.label} onChange={e => editBadgeLabel(i, e.target.value)} maxLength={60}
              className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all"
            />
            <button onClick={() => moveBadge(i, -1)} disabled={i === 0}               className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 transition-colors"><ChevronUp   className="w-3.5 h-3.5 text-gray-500" /></button>
            <button onClick={() => moveBadge(i,  1)} disabled={i === badges.length-1} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 transition-colors"><ChevronDown className="w-3.5 h-3.5 text-gray-500" /></button>
            <button onClick={() => removeBadge(i)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
          </div>
        ))}

        <div className="flex gap-2">
          <input type="text" value={badgeDraft} onChange={e => setBadgeDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addBadge(); } }}
            placeholder="Ex: WhatsApp Disponível" maxLength={60}
            className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 transition-all" />
          <button onClick={addBadge} className="flex items-center gap-1.5 bg-gray-100 hover:bg-brand-primary/10 text-gray-600 hover:text-brand-primary text-xs font-medium px-3 py-1.5 rounded-lg transition-colors">
            <Plus className="w-3.5 h-3.5" /> Adicionar
          </button>
        </div>
      </div>

      <SaveBar dirty={dirty} saving={saving} onSave={handleSave} />
    </div>
  );
}

// ── Channel editor row ─────────────────────────────────────────────────────────
function ChannelRow({
  channel, index, total,
  onChange, onDelete, onMove,
}: {
  channel: ContactChannel;
  index: number; total: number;
  onChange: (c: ContactChannel) => void;
  onDelete: () => void;
  onMove: (dir: -1 | 1) => void;
}) {
  const [open, setOpen] = useState(false);
  const meta = CHANNEL_META[channel.type];
  const Icon = meta.icon;

  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden">
      {/* Header row */}
      <div className="flex items-center gap-2 px-4 py-3 bg-gray-50">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${meta.iconBg}`}>
          <Icon className={`w-4 h-4 ${meta.iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-700 truncate">{channel.label || meta.label}</p>
          <p className="text-xs text-gray-400 truncate">{channel.value || '—'}</p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => onMove(-1)} disabled={index === 0}       className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 transition-colors"><ChevronUp   className="w-3.5 h-3.5 text-gray-500" /></button>
          <button onClick={() => onMove(1)}  disabled={index === total-1} className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 transition-colors"><ChevronDown className="w-3.5 h-3.5 text-gray-500" /></button>
          <button
            onClick={() => onChange({ ...channel, enabled: !channel.enabled })}
            className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full transition-colors ${
              channel.enabled ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
            }`}
          >
            {channel.enabled ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
            {channel.enabled ? 'Visível' : 'Oculto'}
          </button>
          <button onClick={() => setOpen(v => !v)} className="p-1 rounded hover:bg-gray-200 transition-colors">
            <Pencil className="w-3.5 h-3.5 text-brand-primary" />
          </button>
          <button onClick={onDelete} className="p-1 rounded hover:bg-red-50 transition-colors">
            <Trash2 className="w-3.5 h-3.5 text-red-400" />
          </button>
        </div>
      </div>

      {/* Expanded editor */}
      {open && (
        <div className="px-4 py-4 space-y-3 border-t border-gray-100 bg-white">
          <div className="grid grid-cols-2 gap-3">
            {/* Type */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Tipo de canal</label>
              <select
                value={channel.type}
                onChange={e => onChange({ ...channel, type: e.target.value as ChannelType })}
                className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 bg-white"
              >
                {CHANNEL_TYPES.map(t => (
                  <option key={t} value={t}>{CHANNEL_META[t].label}</option>
                ))}
              </select>
            </div>
            {/* Label */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Rótulo exibido</label>
              <input type="text" value={channel.label} onChange={e => onChange({ ...channel, label: e.target.value })}
                placeholder={meta.label} maxLength={40}
                className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all" />
            </div>
          </div>

          {/* Value */}
          <div className="space-y-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {channel.type === 'whatsapp' || channel.type === 'phone' ? 'Número' : channel.type === 'email' ? 'E-mail' : 'URL / Link'}
            </label>
            <p className="text-xs text-gray-400">{meta.hint}</p>
            <input type="text" value={channel.value} onChange={e => onChange({ ...channel, value: e.target.value })}
              placeholder={meta.placeholder} maxLength={300}
              className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all" />
          </div>

          {/* Description */}
          <div className="space-y-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Descrição</label>
            <input type="text" value={channel.description} onChange={e => onChange({ ...channel, description: e.target.value })}
              placeholder="Frase curta exibida abaixo do valor" maxLength={120}
              className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all" />
          </div>

          {/* CTA */}
          <div className="space-y-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Texto do botão</label>
            <input type="text" value={channel.cta} onChange={e => onChange({ ...channel, cta: e.target.value })}
              placeholder="Ex: Enviar mensagem" maxLength={50}
              className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all" />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Canais tab ─────────────────────────────────────────────────────────────────
function CanaisTab() {
  const { toast } = useToast();
  const [channels, setChannels] = useState<ContactChannel[]>(DEFAULT_CHANNELS);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [dirty, setDirty]       = useState(false);

  useEffect(() => {
    settingsApi.list()
      .then(list => {
        const raw = list.find(s => s.key === 'contact_channels_config')?.value;
        setChannels(parseChannels(raw));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const update = (updated: ContactChannel) => {
    setChannels(prev => prev.map(c => c.id === updated.id ? updated : c));
    setDirty(true);
  };
  const remove = (id: string) => { setChannels(prev => prev.filter(c => c.id !== id)); setDirty(true); };
  const move = (index: number, dir: -1 | 1) => {
    setChannels(prev => {
      const arr = [...prev];
      const ni = index + dir;
      if (ni < 0 || ni >= arr.length) return arr;
      [arr[index], arr[ni]] = [arr[ni], arr[index]];
      return arr;
    });
    setDirty(true);
  };
  const add = (type: ChannelType) => {
    const meta = CHANNEL_META[type];
    const id = `${type}_${Date.now()}`;
    setChannels(prev => [...prev, { id, type, label: meta.label, value: '', description: '', cta: '', enabled: true }]);
    setDirty(true);
  };

  const [showPicker, setShowPicker] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.update('contact_channels_config', JSON.stringify(channels));
      toast({ title: 'Canais salvos!' });
      setDirty(false);
    } catch {
      toast({ title: 'Erro ao salvar', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-brand-primary" /></div>;

  return (
    <div className="space-y-4 bg-white border border-gray-100 rounded-xl p-4">
      <SectionHeader icon={Phone} title="Canais de Contato" description="Cards de contato exibidos na página /contato" />

      <div className="space-y-2">
        {channels.map((channel, index) => (
          <ChannelRow
            key={channel.id}
            channel={channel}
            index={index}
            total={channels.length}
            onChange={update}
            onDelete={() => remove(channel.id)}
            onMove={dir => move(index, dir)}
          />
        ))}
        {channels.length === 0 && (
          <p className="text-sm text-gray-400 italic text-center py-4">Nenhum canal. Adicione um abaixo.</p>
        )}
      </div>

      {/* Add channel picker */}
      {showPicker ? (
        <div className="border-2 border-brand-primary/20 rounded-2xl bg-white p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-gray-600 uppercase tracking-wide">Escolha o tipo de canal</p>
            <button onClick={() => setShowPicker(false)} className="text-gray-400 hover:text-gray-600 text-xs underline">Cancelar</button>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {CHANNEL_TYPES.map(type => {
              const meta = CHANNEL_META[type];
              const Icon = meta.icon;
              return (
                <button key={type} onClick={() => { add(type); setShowPicker(false); }}
                  className="flex flex-col items-center gap-1.5 rounded-xl p-3 border border-gray-100 hover:border-brand-primary/30 hover:bg-brand-primary/5 hover:scale-105 transition-all cursor-pointer">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${meta.iconBg}`}>
                    <Icon className={`w-4 h-4 ${meta.iconColor}`} />
                  </div>
                  <span className="text-[10px] font-medium text-gray-500 leading-tight text-center">{meta.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <button onClick={() => setShowPicker(true)}
          className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 hover:border-brand-primary/40 hover:bg-brand-primary/5 text-gray-400 hover:text-brand-primary text-sm font-medium py-2.5 rounded-xl transition-all">
          <Plus className="w-4 h-4" /> Adicionar canal
        </button>
      )}

      <SaveBar dirty={dirty} saving={saving} onSave={handleSave} />
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────
export function ContatoManager() {
  const [activeTab, setActiveTab] = useState<'cabecalho' | 'canais'>('cabecalho');
  const tabs = [
    { id: 'cabecalho' as const, label: 'Cabeçalho', icon: MessageCircle },
    { id: 'canais'    as const, label: 'Canais',    icon: Phone },
  ];
  return (
    <div className="space-y-6">
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${activeTab === tab.id ? 'bg-white text-brand-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            <tab.icon className="w-3.5 h-3.5 shrink-0" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>
      {activeTab === 'cabecalho' && <CabecalhoTab />}
      {activeTab === 'canais'    && <CanaisTab />}
    </div>
  );
}
